import { css, html, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

interface Room {
    name: string;
    images: string[];
    position: {
        x: number;
        y: number;
    };
    visited: boolean;
}
/** CSS affecting the {@link MapComponent} */
const styles: string = css`
  :host {
    height: 56%;
    margin-top: 12.5%;
  }
  .map-component {
    height: 100%;
  }
  .map {
    background: rgba(41, 35, 35, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    gap: 12px;
    height: 100%;
    padding: 20px;
    overflow: auto;
    overflow-x: hidden;
  }
  .map h3 {
    justify-self: center;
    margin-top: 10px;
  }
  .rooms-container {
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 20px;
    padding: 20px;
  }

  .map-svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none; 
  }
  .room {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 12px;
    text-align: center;
    color: #fff;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer; /* Add cursor pointer to indicate clickability */
  }
  .room.visited {
    background: rgba(0, 200, 0, 0.5); /* Green for visited rooms */
  }
  .room.not-visited {
    background: rgba(100, 100, 100, 0.5); /* Grey for non-visited rooms */
  }
  .room.current-room {
    background: rgba(255, 0, 0, 0.6); /* Red for the current room */
}
  .room:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
  }
  .room img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 8px;
  }
  .room .name {
    font-size: 1rem;
    font-weight: bold;
    color: #f5f5f5;
  }
  .no-rooms {
    text-align: center;
    color: #fff;
    font-size: 1.2rem;
    font-weight: bold;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    animation: fadeIn 0.5s ease-in-out;
  }

  .map-popup.show {
    display: block; 
  }
  
  .open-map-button {
    background: #4caf50;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
  }
  
  .map-popup {
    display: none;
    position: fixed;
    top: 80%;
    left: -150%;
    transform: translate(-50%, -50%);
    background: rgba(41, 35, 35, 0.85); /* Fallback background */
    background-image: url("assets/img/rooms/map.png"); /* Background image */
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 20px;
    z-index: 1; /* Ensure it's behind the rooms */
    width: 80vw; 
    height: 60vh;
    position: relative; /* Ensures z-index works */
  }
  
  .hidden {
    display: none;
  }
  
  .close-button {
    background: red;
    color: white;
    border: none;
    padding: 5px 10px;
    font-size: 1rem;
    cursor: pointer;
    border-radius: 5px;
  }
`;

export class MapComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private _rooms: Array<{ name: string; images: string[]; position: { x: number; y: number }; visited: boolean }> = [];
    private popup: HTMLElement | null = null;
    private currentRoom: string | null = null; // Add this line to store the current room

    public constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    public connectedCallback(): void {
        void this.render().then(() => {
            this.popup = this.shadowRoot?.querySelector(".map-popup") || null;
            this.setupEventListeners();

            // Select buttons
            const openButton: HTMLElement | null | undefined = this.shadowRoot?.querySelector(".open-map-button");
            const closeButton: HTMLElement | null | undefined = this.shadowRoot?.querySelector(".close-button");

            // Ensure event listeners are added correctly
            openButton?.addEventListener("click", () => {
                this.popup?.classList.add("show");
            });

            closeButton?.addEventListener("click", () => {
                this.popup?.classList.remove("show");
            });

            document.addEventListener("keydown", (event: KeyboardEvent) => {
                if (event.key === "Escape") {
                    this.popup?.classList.remove("show");
                }
            });
        });
    }

    /**
 * Sets up event listeners for opening/closing the map
 */
    private setupEventListeners(): void {
        const closeButton: HTMLElement | null | undefined = this.shadowRoot?.querySelector(".close-button");

        closeButton?.addEventListener("click", () => {
            this.popup?.classList.remove("show");
        });

        document.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                this.popup?.classList.remove("show");
            }
        });
    }

    public async render(): Promise<void> {
        await this.loadRooms();
        const element: HTMLElement = this.renderMap();
        if (!this.shadowRoot) return;
        this.shadowRoot.innerHTML = "";
        this.shadowRoot.append(element);
    }

    private async loadRooms(): Promise<void> {
        try {
            const roomsData: {
                rooms: Room[];
                currentRoom?: string; // Added current room to the response
            } = await this._gameRouteService.getRooms();

            this._rooms = roomsData.rooms;

            // Set the current room for highlighting
            this.currentRoom = roomsData.currentRoom || null; // Save the current room
        }
        catch (error) {
            console.error("Failed to load rooms:", error);
        }
    }

    private renderMap(): HTMLElement {
        return html`
          <div class="map-component">
            <style>${styles}</style>
            <button class="open-map-button">🗺️</button>
            <div class="map-popup">
              <button class="close-button">X</button>
              <h3>Map</h3>
              <div class="rooms-container">
                ${this._rooms.length === 0 ? this.renderNoRooms() : this.renderRooms()}
              </div>
            </div>
          </div>`;
    }

    private renderRooms(): HTMLElement[] {
        return htmlArray`
            ${this._rooms.map(room => html`
                <div class="room ${room.visited ? "visited" : "not-visited"} ${room.name === this.currentRoom ? "current-room" : ""}"
                    style="position: absolute; left: ${room.position.x}px; top: ${room.position.y}px; "
                    @click="${() => this.onRoomClick(room.name)}">
                    ${this.renderImage(room.images).outerHTML}
                    <div class="name">${room.name}</div>
                </div>
            `)}
        `;
    }

    /**
   * Handle the click event to move to a new room
   */
    private async onRoomClick(roomName: string): Promise<void> {
        try {
            await this._gameRouteService.moveRoom(roomName);
            await this.loadRooms();
            void this.render();
            console.log(`Moved to ${roomName}`);
        }
        catch (error) {
            console.error(`Failed to move to ${roomName}:`, error);
        }
    }

    /**
   * Updates the visited status of a room based on its name.
   */
    public setRoomVisited(roomName: string): void {
        const room: Room | undefined = this._rooms.find(r => r.name === roomName);
        if (room) {
            room.visited = true;
            void this.render(); // Re-render to apply the visual change
        }
    }

    private renderImage(images: string[]): HTMLElement {
        const imgSrc: string = images.length > 0 ? images[0] : "itemDefault";
        return html`
      <img src="assets/img/rooms/${imgSrc}.png" onerror="this.src='assets/img/rooms/itemDefault.png'" />
    `;
    }

    private renderNoRooms(): HTMLElement {
        return html`<div class="no-rooms">No rooms available on the map</div>`;
    }
}
